import { MutationTree } from 'vuex';
import {
  GroupInfo,
  ChanCateBaseInfo,
  GroupUserInfo,
  AdvConfList,
  EntranceConfList,
  TopicInfo,
  ABTestReportInfo,
  GroupBaseInfo,
} from '_c/typings/proto';
import { State } from '@/store/modules/group/state';
import { storageKey, StorageManager } from '_c/business/storage';
import { logger } from '_c/util/logger';
import { IMsg } from '_c/typings/model/im';

interface ILastMessageOptions {
  groupId: string;
  channelId: string;
  message: IMsg;
}

interface ICountOptions {
  groupId: string;
  channelId: string;
  count?: number;
}

interface IGroupUserInfoOptions {
  groupId: string;
  info: GroupUserInfo;
}

interface ITopicListOptions {
  groupId: string;
  channelId: string;
  topicInfo: TopicInfo;
  isDelete?: boolean;
}

function setChannelList(state: State, groupList: GroupInfo[]) {
  groupList.forEach((group) => {
    state.channelListWithCategory[group.info.gid] = [];
    state.channelList[group.info.gid] = [];
    group.info.cateList.forEach((cate) => {
      state.channelList[group.info.gid] = group.chanList.filter(item => item.isCmdChan === 0);
      state.channelListWithCategory[group.info.gid].push({
        id: cate.caid,
        name: cate.name,
        channelList: state.channelList[group.info.gid].filter(item => item.caid === cate.caid),
      });
    });
    const noCateChannelList = state.channelList[group.info.gid].filter(item => item.caid === '0');
    if (noCateChannelList.length > 0) {
      state.channelListWithCategory[group.info.gid].splice(0, 0, {
        id: '0',
        name: '',
        channelList: noCateChannelList,
      });
    }
  });
}

/**
 * 存储频道折叠配置
 * @param state 存储
 * @param channelFoldData 频道折叠配置
 */
function setChannelFoldData(state: State, channelFoldData: Record<string, Array<ChanCateBaseInfo>>) {
  state.channelFoldData = channelFoldData;
  StorageManager.getInstance()
    .set<Record<string, Array<ChanCateBaseInfo>>>(storageKey.getChannelFoldDataKey(), state.channelFoldData);
}

const mutations: MutationTree<State> = {
  setGroupList(state, groupList: GroupInfo[]) {
    logger.info('mutation groupList', groupList);
    state.groupList = groupList;
  },
  // 向state中设置用户的推广位信息列表
  setPromotionList(state, promotionList: Record<string, AdvConfList>) {
    state.promotionList = Object.assign(state.promotionList, promotionList);
  },
  updateChannelFoldData(state, id) {
    const group = state.groupList.find(group => group.info.gid === state.selectGroupId);
    const caItem = group?.info?.cateList.find(channel => channel.caid === id);
    if (caItem) {
      caItem.isFold = !caItem?.isFold;
    }
  },
  // 设置首次加入群组标识
  setFirstJoin(state, { gid, isFirstJoin }: {gid: string, isFirstJoin: boolean}) {
    state.firstJoinMap[gid] = isFirstJoin;
  },
  // 设置欢迎弹窗是否展示
  setWelcomeGroupInfo(state, { gid, groupInfo }: {gid: string, groupInfo: GroupBaseInfo}) {
    state.welcomeGroupInfoMap[gid] = groupInfo;
  },
  // 设置上报信息
  setABTestReportData(state, { gid, reportData }: {gid: string, reportData: ABTestReportInfo}) {
    state.abTestReportInfoMap[gid] = reportData;
  },
  // 设置频道折叠信息
  resetChannelFoldData(state, cateConfig) {
    const tempChannelFoldData: Record<string, Array<ChanCateBaseInfo>> = {};
    // delete necessary gid
    const gidList = Object.keys(cateConfig);
    Object.keys(state.channelFoldData).forEach((item) => {
      if (gidList.indexOf(item) > -1) {
        tempChannelFoldData[item] = state.channelFoldData[item];
      }
    });
    const gidList2 = Object.keys(tempChannelFoldData);
    Object.keys(cateConfig).forEach((item) => {
      if (gidList2.indexOf(item) === -1) {
        tempChannelFoldData[item] = cateConfig[item];
      }
    });
    setChannelFoldData(state, tempChannelFoldData);
  },
  setChannelFoldData(state, cateConfig) {
    const tempChannelFoldData: Record<string, Array<ChanCateBaseInfo>> = { ...state.channelFoldData };
    const gidList = Object.keys(state.channelFoldData);
    Object.keys(cateConfig).forEach((item) => {
      if (gidList.indexOf(item) === -1) {
        tempChannelFoldData[item] = cateConfig[item];
      }
    });
    setChannelFoldData(state, tempChannelFoldData);
  },
  unsetChannelFoldData(state, gidList) {
    const tempChannelFoldData: Record<string, Array<ChanCateBaseInfo>> = {};
    Object.keys(state.channelFoldData).forEach((item) => {
      if (gidList.indexOf(item) === -1) {
        tempChannelFoldData[item] = state.channelFoldData[item];
      }
    });
    setChannelFoldData(state, tempChannelFoldData);
  },
  switchChannelFoldStatus(state, { gid, cateId }: {gid: string, cateId: string}) {
    if (gid && state.channelFoldData[gid]) {
      const tempChannelFoldData = {
        ...state.channelFoldData,
        [gid]: state.channelFoldData[gid].map((item) => {
          if (item.caid === cateId) {
            return {
              ...item,
              isFold: !item.isFold,
            };
          }
          return {
            ...item,
          };
        }),
      };
      setChannelFoldData(state, tempChannelFoldData);
    }
  },
  // 向state中设置用户的中心化入口信息列表
  setCenterEntranceList(state, centerEntranceList: Record<string, EntranceConfList>) {
    state.centerEntranceList = Object.assign(state.centerEntranceList, centerEntranceList);
  },
  setChannelList(state, groupList: GroupInfo[]) {
    return setChannelList(state, groupList);
  },
  setSelectGroupId(state, groupId: string) {
    if (!groupId) {
      state.selectGroupId = '';
    } else if (groupId !== state.selectGroupId) {
      const group = state.groupList.find(group => group.info.gid === groupId);
      if (group) {
        state.selectGroupId = groupId;
      }
    }
  },
  setSelectChannelId(state, channelId: string) {
    state.selectChannelId = channelId;
  },
  setLastMessage(state, { groupId, channelId, message }: ILastMessageOptions) {
    state.lastMessageList[groupId] = state.lastMessageList[groupId] || {};
    state.lastMessageList[groupId][channelId] = message;
  },
  setAtCount(state, { groupId, channelId, count }: ICountOptions) {
    state.atCountList[groupId] = state.atCountList[groupId] || {};
    state.atCountList[groupId][channelId] = count || 0;
  },
  setAtMeCount(state, { groupId, channelId, count }: ICountOptions) {
    state.atMeList[groupId] = state.atMeList[groupId] || {};
    state.atMeList[groupId][channelId] = count || 0;
  },
  setUnreadCount(state, { groupId, channelId, count }: ICountOptions) {
    state.unreadCountList[groupId] = state.unreadCountList[groupId] || {};
    state.unreadCountList[groupId][channelId] = count || 0;
  },
  updateGroupInfo(state, data: GroupInfo) {
    const groupId = data.info.gid;
    const index = state.groupList.findIndex(group => group.info.gid === groupId);
    if (index >= 0) {
      state.groupList[index] = { ...state.groupList[index], ...data };
      state.groupList[index].isUpdate = true;
    } else {
      state.groupList.push(data);
    }
  },
  setGroupCurrentUserInfo(state, { groupId, info }: IGroupUserInfoOptions) {
    const group = state.groupList.find(group => group.info.gid === groupId);
    if (group) {
      group.currentUserInfo = info;
    }
  },
  quitGroup(state, groupId: string) {
    const index = state.groupList.findIndex(group => group.info.gid === groupId);
    if (index >= 0) {
      const group = state.groupList[index];
      if (group.info.gid === state.selectGroupId) {
        delete state.promotionList[groupId];
        delete state.centerEntranceList[groupId];
      }
      // 删除群组
      state.groupList.splice(index, 1);
    }
  },
  setTopicList(state, data: ITopicListOptions) {
    state.topicList[data.groupId] = state.topicList[data.groupId] || {};
    state.topicList[data.groupId][data.channelId] = state.topicList[data.groupId][data.channelId] || [];
    const list = state.topicList[data.groupId][data.channelId];
    const index = list.findIndex(item => item.topicId === data.topicInfo.topicId);
    if (data.isDelete) {
      if (index >= 0) {
        list.splice(index, 1);
      }
    } else {
      if (index < 0) {
        list.push(data.topicInfo);
      } else {
        list.splice(index, 1, data.topicInfo);
      }
    }
  },
};

export default mutations;
